<template lang="pug">
div
	h1 {{contentpage.name}}
	ws-content(:value="contentpage.content" )
		client-only
			mapbox.mb-4(
				ref="map"
				style="height:400px"
				:layers="layers"
				:sources="sources"
				:fitbounds="markers.features.map(e=>e.geometry.coordinates)"
				:geolocateControl="{ show: true, position: 'top-right', options: {}}"
				@map-styleload="mapStyle"
				@map-init="addGeocoder")
				div(slot='popup')
					span.h3 {{popupData.name}}
		v-data-iterator(:items="localdestinations_with_location" hide-default-footer disable-pagination :options.sync="options")
			template(#item="{item}")
				localdestination-list-item(v-bind="item" type="organisation")
		template(v-if="localdestinations_without_location.length")
			h2.mb-3 Organisaties zonder eigen locatie
			v-data-iterator(:items="localdestinations_without_location" hide-default-footer disable-pagination :options.sync="options")
				template(#item="{item}")
					localdestination-list-item(v-bind="item" type="organisation")
					v-divider.my-3

</template>
<style lang="sass">
@import '@mapbox/mapbox-gl-geocoder/dist/mapbox-gl-geocoder.css'
</style>

<script>
import gql from 'graphql-tag';
import { ref } from '@vue/composition-api';
import Content from '@dengl/common/components/content.vue';
import LocaldestinationListItem from '@/components/localdestinations/list-item.js';
import LocaldestinationListMixin from '@/components/localdestinations/list-mixin.js';
import GeocoderMixin from '@/mixins/mapbox-geocoder.js';
import MetaFragment from '@/components/graphql/metaFragment.graphql';
import useMap from '@/use/map';

const query = gql`
	query getLocaldestinationindexpage($slug: String!) {
		contentpage(slug: $slug) {
			id
			name
			slug
			content {
				...ContentDataFragment
			}
			...MetaFragment
		}
		localdestinations {
			name
			slug
			logo
			... on Skihall {
				reviews {
					voteCount
					average
				}
			}
			... on Dryslope {
				reviews {
					voteCount
					average
				}
			}
			... on Organisation {
				reviews {
					voteCount
					average
				}
			}
			... on Revolvingslope {
				reviews {
					voteCount
					average
				}
			}
			address {
				city
				street
				phone
				postcode
				countrycode
				country
				url
			}
			photos {
				url
			}
			tags
			content {
				introduction(length: 180)
			}
			geography {
				location {
					type
					coordinates
				}
			}
		}
	}
	${MetaFragment}
	${Content.fragments}
`;

export default {
	name: 'LocaldestinationsIndex',
	components: {
		LocaldestinationListItem,
		Mapbox: () => (process.client ? import('@/components/mapbox.vue') : null)
	},
	mixins: [LocaldestinationListMixin, GeocoderMixin],
	nuxtI18n: {
		paths: {
			'nl-NL': '/wintersport-in-de-buurt',
			'nl-BE': '/wintersport-in-de-buurt'
		}
	},
	setup() {
		const destinations = ref([]);
		const { markers, sources, attachListeners, popupData } =
			useMap(destinations);

		const layers = [
			{
				id: 'destination-markers',
				type: 'circle',
				source: 'geojson-markers',
				layout: {
					visibility: 'visible'
				},
				paint: {
					'circle-radius': [
						'case',
						['boolean', ['feature-state', 'hover'], false],
						7,
						5
					],
					'circle-color': 'rgb(79, 164, 221)',
					'circle-stroke-color': '#fff',
					'circle-stroke-width': 2
				},
				minzoom: 0,
				maxzoom: 7
			},
			{
				id: 'destination-markers-icons',
				type: 'symbol',
				source: 'geojson-markers',
				layout: {
					'icon-image': ['get', 'icon'],
					'icon-size': 0.3,
					'icon-anchor': 'bottom',
					'icon-allow-overlap': true
				},
				minzoom: 7,
				maxzoom: 24
			}
		];

		return {
			destinations,
			sources,
			layers,
			markers,
			attachListeners,
			popupData
		};
	},
	asyncData({ route, app }) {
		app.ads({ navigation: { visible: true, type: 'spot' } });

		const variables = {
			slug: route.path.split('/').pop()
		};
		return app.$graphClient
			.query({
				query,
				variables,
				errorPolicy: 'all'
			})
			.then(({ data, errors }) => {
				if (errors) {
					throw errors[0];
				}
				app.meta(data.contentpage.meta);
				app.breadcrumbs({
					[data.contentpage.name]: {
						name: 'localdestinations'
					}
				});
				return data;
			})
			.catch(error => {
				return app.httpError(500, null, 'localdestinations-index-01', error);
			});
	},
	data() {
		return {
			options: {
				sortBy: 'name'
			}
		};
	},
	head() {
		return {
			...this.$structuredData()
		};
	},
	mounted() {
		this.destinations = this.localdestinations_with_location;
		if (this.$refs.map) {
			this.$nextTick(() => {
				this.$refs.map.fitToCurrentBounds();
			});
		}
	},
	methods: {
		mouseClick(feature) {
			this.$router.push(feature.properties.to);
		},
		mapStyle(map) {
			this.$refs.map.fitToCurrentBounds();
			this.attachListeners(
				map,
				this.$refs.map,
				'destination-markers',
				'geojson-markers',
				this.mouseClick
			);
			this.attachListeners(
				map,
				this.$refs.map,
				'destination-markers-icons',
				'geojson-markers',
				this.mouseClick
			);
		}
	}
};
</script>
